/**
 * LIFF composable — singleton integration with LINE's in-app browser
 * (LINE Front-end Framework).
 *
 * Two consumers:
 *  - Seamless login (bead lucky-war-hw7.5, design: docs/plans/LIFF-INTEGRATION.md):
 *    on boot, when running inside LINE, exchange the LIFF-issued token for the
 *    PocketBase session via POST /api/auth/line/liff — no login button.
 *  - Share-to-chat (bead lucky-war-trko): send a Flex bubble built by
 *    lib/shareFlex.ts to the chat the app was opened from.
 *
 * Cost discipline for the normal browser PWA: the LIFF SDK (~200 KB) is only
 * imported when BOTH (a) VITE_LIFF_ID is configured at build time and (b) the
 * user agent looks like LINE's in-app browser ("Line/"). Everyone else takes
 * the zero-cost path and never downloads the SDK.
 */
import { ref, shallowRef } from 'vue';
import { SHARE_SCHEMA_VERSION, buildAltText, buildKpiFlex, type KpiCardShareInput } from '@/lib/shareFlex';

export type LiffReadyState = 'unsupported' | 'initializing' | 'ready' | 'error';

/**
 * Where the app was opened from (liff.getContext().type):
 *  - utou/group/room → a chat with the OA; sendMessages() lands in that chat
 *  - none           → rich menu / home, no chat target; use shareTargetPicker
 *  - external       → plain browser (SDK path disabled — see UA gate)
 */
export type LiffContextType = 'utou' | 'group' | 'room' | 'none' | 'external';

const LIFF_ID = (import.meta.env.VITE_LIFF_ID ?? '').trim();

// Module-level singleton state (same pattern as useAuth).
const readyState = shallowRef<LiffReadyState>(LIFF_ID ? 'initializing' : 'unsupported');
const inClient = ref(false);
const contextType = ref<LiffContextType | null>(null);
/**
 * Last share failure's raw error text (err.message from the SDK), exposed so
 * the UI can show a precise hint and the operator can relay it. null when the
 * last attempt succeeded / none happened.
 */
const lastShareError = ref<string | null>(null);

let initPromise: Promise<boolean> | null = null;

type LiffSdk = typeof import('@line/liff')['default'];

/** UA gate: LINE's in-app browser always advertises "Line/" in the UA string. */
function looksLikeLineApp(): boolean {
  return typeof navigator !== 'undefined' && /Line\//i.test(navigator.userAgent);
}

/**
 * Initializes LIFF exactly once. Returns true when running inside LINE's
 * in-app browser AND init succeeded — the signal callers use to take the LIFF
 * path (seamless login / share buttons). Never throws.
 */
export function initLiff(): Promise<boolean> {
  if (initPromise) return initPromise;

  if (!LIFF_ID || !looksLikeLineApp()) {
    readyState.value = 'unsupported';
    initPromise = Promise.resolve(false);
    return initPromise;
  }

  initPromise = (async () => {
    try {
      const liff: LiffSdk = (await import('@line/liff')).default;
      await liff.init({ liffId: LIFF_ID });
      inClient.value = liff.isInClient();
      const ctx = liff.getContext();
      contextType.value = (ctx?.type as LiffContextType | undefined) ?? null;
      readyState.value = 'ready';
      return inClient.value;
    } catch {
      // Invalid LIFF ID, network failure, unsupported client — the caller
      // falls back to the standard bootstrapAuth() flow (LoginView).
      readyState.value = 'error';
      return false;
    }
  })();

  return initPromise;
}

/** Result of the LIFF token → PocketBase session exchange. */
export type LiffExchangeResult = 'approved' | 'pending' | 'failed';

// Re-try budget for the first-open race observed on device (2026-09-04):
// right after the LIFF consent round trip, liff.init() can resolve while the
// session/token is not yet readable (isLoggedIn() false, tokens null). A short
// wait + idempotent re-init lets the SDK finish its bookkeeping instead of
// dropping to LoginView — which then forced a full OAuth round trip (and a
// SECOND consent screen, because /login sends prompt=consent).
const TOKEN_RACE_WAIT_MS = 900;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Exchanges the current LIFF token for a PocketBase session cookie
 * (POST /api/auth/line/liff — server route reuses upsertUser + the approval
 * gate, so the semantics match the OAuth callback exactly). Only meaningful
 * after initLiff() returned true.
 *
 * `liff.login()` is intentionally NOT used in the LIFF browser (it errors or
 * no-ops there — the browser is supposed to be auto-logged-in); the recovery
 * for the not-yet-logged-in race is wait + idempotent re-init.
 */
export async function liffSessionExchange(): Promise<LiffExchangeResult> {
  try {
    const liff: LiffSdk = (await import('@line/liff')).default;

    if (!liff.isLoggedIn()) {
      // First-open race: the consent round trip just completed. Give the SDK
      // a moment and re-init (idempotent) before declaring failure.
      await sleep(TOKEN_RACE_WAIT_MS);
      await liff.init({ liffId: LIFF_ID });
      if (!liff.isLoggedIn()) return 'failed';
    }

    let accessToken = liff.getAccessToken();
    let idToken = liff.getIDToken();
    if (!accessToken && !idToken) {
      // Token-availability race on the same first load — same recovery.
      await sleep(TOKEN_RACE_WAIT_MS);
      await liff.init({ liffId: LIFF_ID });
      accessToken = liff.getAccessToken();
      idToken = liff.getIDToken();
      if (!accessToken && !idToken) return 'failed';
    }

    const res = await fetch('/api/auth/line/liff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: accessToken ?? '', id_token: idToken ?? '' }),
    });
    if (res.status === 200) return 'approved';
    if (res.status === 403) return 'pending';
    return 'failed';
  } catch {
    return 'failed';
  }
}

/** Delivery outcome of a share attempt. */
export type ShareResult = 'sent' | 'shared' | 'cancelled' | 'unsupported' | 'error';

/**
 * Sends a Flex bubble to the chat the LIFF app was opened from
 * (utou/group/room contexts). Outside a chat (rich menu / home), falls back to
 * LINE's share target picker so the user picks the destination themselves.
 * Requires the `chat_message.write` scope on the LIFF app.
 */
/**
 * Best-effort share diagnostics (temporary, 2026-09-04 debugging): rides the
 * existing GET /api/auth/line/status with a `liffdiag` query so the outcome,
 * payload version (stale-bundle detection), and LINE's raw error text land in
 * the PocketBase request logs — the share call itself goes client→LINE and is
 * otherwise invisible server-side. Never throws, never blocks the share.
 */
function reportShareDiag(kind: string, detail = ''): void {
  try {
    const q = new URLSearchParams({ liffdiag: kind, v: SHARE_SCHEMA_VERSION });
    if (detail) q.set('d', detail.slice(0, 90));
    void fetch(`/api/auth/line/status?${q.toString()}`, { keepalive: true }).catch(() => undefined);
  } catch {
    /* diagnostics must never break the share path */
  }
}

/** True when the last share error looks like a missing permission/scope. */
export function isSharePermissionError(): boolean {
  const m = (lastShareError.value ?? '').toLowerCase();
  return m.includes('permission') || m.includes('403') || m.includes('scope') || m.includes('denied') || m.includes('unauthorized');
}

/**
 * Absolute LIFF URL that reopens the app inside LINE's LIFF browser at the
 * current SPA page. Tapping it in a chat
 * is the redirect path into the LIFF experience; a plain website URL would
 * open LINE's generic in-app browser instead (no LIFF SDK, no seamless
 * login). Falls back to the hand-built form if permanentLink is unavailable.
 */
async function liffDeepLink(path?: string): Promise<string> {
  try {
    const liff: LiffSdk = (await import('@line/liff')).default;
    // Use LINE's recommended URL-explicit API. This runs only after init, so
    // the SDK has already removed credentials from the redirect URL.
    return liff.permanentLink.createUrlBy(window.location.href);
  } catch {
    return `https://liff.line.me/${LIFF_ID}${path && path !== '/' ? path : ''}`;
  }
}

/**
 * Sends one message object via sendMessages (chat context) or
 * shareTargetPicker. 'cancelled' means the user dismissed the picker —
 * not an error; callers must stop, not retry.
 */
async function deliverOnce(liff: LiffSdk, message: unknown): Promise<'sent' | 'cancelled'> {
  const ctx = contextType.value;
  if (ctx === 'utou' || ctx === 'group' || ctx === 'room') {
    await liff.sendMessages(message as Parameters<typeof liff.sendMessages>[0]);
    return 'sent';
  }
  const res = await liff.shareTargetPicker(message as Parameters<typeof liff.shareTargetPicker>[0]);
  return res?.status === 'success' ? 'sent' : 'cancelled';
}

/**
 * Share a KPI card (v5): the body-only Flex bubble for visuals plus a TEXT
 * message carrying the LIFF permanent link. LINE displays the two messages
 * together from one send/share-picker action. If LINE rejects the Flex
 * payload, retry with a text-only summary so the redirect still arrives.
 */
export async function sendShareKpi(input: KpiCardShareInput): Promise<ShareResult> {
  if (readyState.value !== 'ready' || !inClient.value) return 'unsupported';

  const deepLink = input.linkPath ? await liffDeepLink(input.linkPath) : undefined;

  try {
    const liff: LiffSdk = (await import('@line/liff')).default;
    const flex = buildKpiFlex(input);
    const redirectText = deepLink
      ? `เปิดดูใน Lucky War (ใน LINE)\n${deepLink}`
      : buildAltText(input);

    try {
      const messages = [
        { type: 'flex', altText: flex.altText, contents: flex.contents },
        ...(deepLink ? [{ type: 'text', text: redirectText }] : []),
      ] as unknown as Parameters<typeof liff.sendMessages>[0];
      const d = await deliverOnce(liff, messages);
      if (d === 'cancelled') return 'cancelled';
      lastShareError.value = null;
      reportShareDiag('sent');
      return 'sent';
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      lastShareError.value = msg;
      console.warn('[liff] flex share failed:', err);
      reportShareDiag('flex_fail', msg);
    }

    try {
      const fallbackText = `${buildAltText(input)}${input.note ? `\n${input.note}` : ''}${deepLink ? `\n${deepLink}` : ''}`;
      const d = await deliverOnce(liff, [{ type: 'text', text: fallbackText }]);
      if (d === 'cancelled') return 'cancelled';
      lastShareError.value = null;
      reportShareDiag('text_ok');
      return 'sent';
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      reportShareDiag('text_fail', msg);
      throw err;
    }
  } catch {
    return 'error';
  }
}

/** Reactive read-access to the LIFF singleton state. */
export function useLiff() {
  return {
    readyState,
    inClient,
    contextType,
    lastShareError,
    initLiff,
    liffSessionExchange,
    sendShareKpi,
    isSharePermissionError,
  };
}
